import { isValidRedirectPath, stripLocalePrefix } from './loginRedirectPath'

const STORAGE_KEY = 'fire-b2b-redirect-after-login'
const MAX_AGE_MS = 60 * 60 * 1000 // 1 hour

interface RedirectStorageEntry {
  path: string
  createdAt: number
}

export function useLoginRedirect() {
  const localePath = useLocalePath()

  /** Reads the `?redirect=` query param and validates it. Pass `to` when calling from middleware. */
  function getRedirectPath(route?: {
    query: Record<string, string | (string | null)[] | null | undefined>
  }): string | undefined {
    const resolvedRoute = route ?? useRoute()
    const redirect = Array.isArray(resolvedRoute.query.redirect)
      ? resolvedRoute.query.redirect[0]
      : resolvedRoute.query.redirect
    return isValidRedirectPath(redirect) ? redirect : undefined
  }

  /** Saves redirect path to localStorage with a timestamp (for SSO roundtrip). */
  function saveRedirectToStorage(path: string): void {
    if (!isValidRedirectPath(path)) {
      return
    }

    const entry: RedirectStorageEntry = { path, createdAt: Date.now() }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entry))
  }

  /** Reads and removes redirect from localStorage. Returns undefined if expired (>1 hour) or invalid. */
  function consumeRedirectFromStorage(): string | undefined {
    const raw = localStorage.getItem(STORAGE_KEY)
    localStorage.removeItem(STORAGE_KEY)

    const entry = safeJsonParse<RedirectStorageEntry>(raw)

    if (!entry) {
      return undefined
    }

    const isExpired = Date.now() - entry.createdAt > MAX_AGE_MS
    return !isExpired && isValidRedirectPath(entry.path) ? entry.path : undefined
  }

  /** Navigates to redirect target (with locale prefix) or falls back to `/`. */
  async function navigateToRedirect(redirectTo?: string): Promise<void> {
    const target = isValidRedirectPath(redirectTo) ? localePath(redirectTo) : '/'
    await navigateTo(target)
  }

  /**
   * Builds the login URL with an optional `?redirect=` param.
   * Strips the locale prefix from `fullPath` before encoding.
   */
  function buildLoginUrl(fullPath: string): string {
    const loginPath = useRoutePath().login()
    const pathWithoutLocale = stripLocalePrefix(fullPath)

    if (!isValidRedirectPath(pathWithoutLocale)) {
      return loginPath
    }

    return `${loginPath}?redirect=${encodeURIComponent(pathWithoutLocale)}`
  }

  return {
    getRedirectPath,
    saveRedirectToStorage,
    consumeRedirectFromStorage,
    navigateToRedirect,
    buildLoginUrl,
  }
}
